import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import i18n from '../i18n';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * Customer 360 — Phase 2. Owner automation feed API. Backend AutomationController,
 * gated check.feature:crm_customers. Suggest-only: approve runs the stored action
 * (send email / award points / TV celebrate); dismiss silences it. Plain JSON,
 * unwrapped via `res?.data ?? res`.
 */
const FEED_KEY     = 'automationSuggestions';
const SETTINGS_KEY = 'automationSettings';
const unwrap = (res) => res?.data ?? res;
const T = (k) => i18n.t(`automation.toast.${k}`);

export const useAutomationSuggestions = (status = 'pending') =>
  useQuery(
    [FEED_KEY, status],
    async () => unwrap((await axiosInstance.get('/automation/suggestions', { params: { status } })).data),
    { staleTime: 30_000 }
  );

export const useAutomationSettings = () =>
  useQuery(SETTINGS_KEY, async () => unwrap((await axiosInstance.get('/automation/settings')).data), { staleTime: 60_000 });

export const useApproveSuggestion = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.post(`/automation/suggestions/${id}/approve`).then((r) => unwrap(r.data)), {
    onSuccess: (s) => {
      qc.invalidateQueries(FEED_KEY);
      const ok = s?.status === 'done';
      showToast(ok ? 'success' : 'warn', T('title'), ok ? T('approved') : i18n.t('automation.toast.action_failed', { reason: s?.result_note || '' }));
    },
    onError: () => showToast('error', T('title'), T('err')),
  });
};

export const useDismissSuggestion = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.post(`/automation/suggestions/${id}/dismiss`).then((r) => unwrap(r.data)), {
    onSuccess: () => { qc.invalidateQueries(FEED_KEY); showToast('success', T('title'), T('dismissed')); },
    onError: () => showToast('error', T('title'), T('err')),
  });
};

export const useScanNow = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(() => axiosInstance.post('/automation/scan').then((r) => unwrap(r.data)), {
    onSuccess: (res) => {
      qc.invalidateQueries(FEED_KEY);
      showToast('success', T('title'), i18n.t('automation.toast.scanned', { count: res?.created ?? 0 }));
    },
    onError: () => showToast('error', T('title'), T('err')),
  });
};

export const useUpdateAutomationSettings = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((body) => axiosInstance.put('/automation/settings', body).then((r) => unwrap(r.data)), {
    onSuccess: () => { qc.invalidateQueries(SETTINGS_KEY); showToast('success', T('title'), T('settings_saved')); },
    onError: () => showToast('error', T('title'), T('err')),
  });
};
